/**
 * DrillDownTable — Phase 21 reusable drill-down for KPI/Employee cards.
 * Supports: Search · Sort · CSV Export · Open action (deep-link).
 */
import React, { useMemo, useState } from 'react';
import { X, Search, Download, ArrowUpDown, ExternalLink } from 'lucide-react';

const _csvCell = (v) => {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

export const DrillDownTable = ({ open, onClose, title, columns, rows, openUrlKey = 'open_url' }) => {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const filtered = useMemo(() => {
    let r = rows || [];
    if (q.trim()) {
      const needle = q.toLowerCase();
      r = r.filter((row) => columns.some((c) => String(row[c.key] ?? '').toLowerCase().includes(needle)));
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const va = a[sortKey], vb = b[sortKey];
        const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va ?? '').localeCompare(String(vb ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  }, [rows, q, sortKey, sortDir, columns]);

  const exportCsv = () => {
    const header = columns.map((c) => _csvCell(c.label)).join(',');
    const lines = filtered.map((row) => columns.map((c) => _csvCell(row[c.key])).join(','));
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'export').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="drilldown-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center gap-3">
          <h3 className="text-lg font-bold text-gray-800 flex-1">{title}</h3>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="drilldown-search"
              placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg w-56 focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <button data-testid="drilldown-export" onClick={exportCsv}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 border border-emerald-200">
            <Download size={12} /> CSV
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="drilldown-close"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-4 py-2 font-semibold text-gray-700 cursor-pointer select-none"
                      onClick={() => { setSortKey(c.key); setSortDir((d) => sortKey === c.key && d === 'asc' ? 'desc' : 'asc'); }}>
                    <span className="inline-flex items-center gap-1">{c.label} <ArrowUpDown size={11} className="text-gray-400" /></span>
                  </th>
                ))}
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="text-center text-gray-400 py-8">No rows</td></tr>
              ) : filtered.map((row, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`drilldown-row-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2 text-gray-700">
                      {c.format ? c.format(row[c.key], row) : String(row[c.key] ?? '—')}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    {row[openUrlKey] && (
                      <a href={row[openUrlKey]} className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-800 text-xs font-medium" data-testid={`drilldown-open-${i}`}>
                        Open <ExternalLink size={11} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2 border-t text-xs text-gray-500">{filtered.length} of {rows?.length || 0} rows</div>
      </div>
    </div>
  );
};

export default DrillDownTable;
